import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AppRole, AuthPrincipal } from './finlex.models';
import type { User } from 'firebase/auth';
import { firstValueFrom, interval } from 'rxjs';
import { environment } from '../../environments/environment';
import { ExternalNavigation } from './external-navigation.service';
import { FirebaseAuthClient } from './firebase-auth.client';

const APPLICATION_ROLES = ['user', 'admin'] as const satisfies readonly AppRole[];
/** A Cognito user's package is re-checked with Cognito this often; the API's lease on it lasts a day. */
const ENTITLEMENT_CHECK_INTERVAL_MS = 60 * 60 * 1000;
/** How often an open session looks for a due re-check. */
const ENTITLEMENT_POLL_MS = 5 * 60 * 1000;

export type AuthSessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | {
      status: 'authenticated';
      email: string;
      principal: AuthPrincipal;
      /** Display name from Cognito (Firebase user displayName); empty when unavailable. */
      name?: string;
      /** Organization from Cognito; unset for identities that are not Cognito-backed. */
      organization?: string;
    }
  | { status: 'error'; message: string };

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly auth = inject(FirebaseAuthClient);
  private readonly http = inject(HttpClient);
  private readonly navigation = inject(ExternalNavigation);
  private readonly sessionState = signal<AuthSessionState>({ status: 'loading' });
  private resolveReady!: () => void;
  private readyResolved = false;
  private readonly ready = new Promise<void>((resolve) => {
    this.resolveReady = resolve;
  });
  private principalLoad: { uid: string; load: Promise<void> } | null = null;
  /** Bumped whenever the identity changes, so a principal load started before it is dropped. */
  private identityGeneration = 0;
  /** Bumped on every Firebase token change, so a change still awaiting a re-check yields to a newer one. */
  private authChange = 0;
  private entitlementCheck: Promise<void> | null = null;
  /** Throttles retries after a failed re-check; the lease from the last success still applies. */
  private entitlementAttemptAt = 0;

  readonly state = this.sessionState.asReadonly();
  readonly identity = computed(() => {
    const state = this.sessionState();
    if (state.status !== 'authenticated') return null;
    return {
      email: state.email,
      name: state.name ?? '',
      organization: state.organization ?? '',
    };
  });
  readonly hasAccess = computed(() =>
    APPLICATION_ROLES.some((role) => this.authenticatedState()?.principal.roles.includes(role)),
  );
  /** Signed in, but the account holds no application role, e.g. no Säädöspankki package. */
  readonly accessMissing = computed(
    () => this.sessionState().status === 'authenticated' && !this.hasAccess(),
  );
  readonly isAdmin = computed(
    () =>
      this.sessionState().status === 'authenticated' &&
      this.authenticatedState()?.principal.roles.includes('admin') === true,
  );

  constructor() {
    this.auth.setLanguage('fi');
    void this.observeSession();
    interval(ENTITLEMENT_POLL_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.sessionState().status === 'authenticated') void this.renewEntitlementIfDue();
      });
  }

  async whenReady(): Promise<void> {
    await this.ready;
  }

  async login(email: string, password: string): Promise<void> {
    this.sessionState.set({ status: 'loading' });
    try {
      const credential = await this.auth.login(email.trim().toLocaleLowerCase('fi-FI'), password);
      await this.loadPrincipal(credential.user);
    } catch (error) {
      this.signOutLocally();
      throw error;
    }
  }

  async loginWithCustomToken(token: string): Promise<void> {
    this.sessionState.set({ status: 'loading' });
    try {
      const credential = await this.auth.loginWithCustomToken(token);
      await this.loadPrincipal(credential.user);
      if (this.sessionState().status !== 'authenticated') {
        throw new Error('Cognito session could not be verified');
      }
    } catch (error) {
      await this.auth.logout().catch(() => undefined);
      this.signOutLocally();
      throw error;
    }
  }

  async logout(): Promise<void> {
    const source = await this.auth.claim('identitySource').catch(() => undefined);
    await this.auth.logout();
    this.signOutLocally();
    // Signing out of Firebase leaves the Cognito session alive, which would
    // silently sign the same user back in on the next Cognito login.
    if (source === 'cognito') {
      this.navigation.assign(`${environment.cognitoAuthUrl}/logout`);
    }
  }

  /** Ends only the Firebase session, before a login that replaces it. */
  async clearLocalSession(): Promise<void> {
    await this.auth.logout().catch(() => undefined);
    this.signOutLocally();
  }

  async resetPassword(email: string): Promise<void> {
    await this.auth.resetPassword(email.trim().toLocaleLowerCase('fi-FI'));
  }

  async idToken(): Promise<string | null> {
    return this.auth.idToken();
  }

  private async observeSession(): Promise<void> {
    try {
      await this.auth.initializePersistence();
      await this.auth.whenAuthStateReady();
      this.auth.observe((user) => {
        void this.handleAuthChange(user);
      });
    } catch {
      this.sessionState.set({
        status: 'error',
        message: 'Kirjautumispalvelua ei voitu alustaa.',
      });
      this.markReady();
    }
  }

  private async handleAuthChange(user: User | null): Promise<void> {
    const change = ++this.authChange;
    if (!user) {
      this.signOutLocally();
      this.markReady();
      return;
    }
    // A lapsed lease would load a principal without the package; a renewal calls back here.
    await this.renewEntitlementIfDue();
    if (change !== this.authChange) return;
    await this.loadPrincipal(user);
    this.markReady();
  }

  private renewEntitlementIfDue(): Promise<void> {
    this.entitlementCheck ??= this.checkEntitlement().finally(() => {
      this.entitlementCheck = null;
    });
    return this.entitlementCheck;
  }

  /**
   * Re-checks a Cognito user's package with Cognito once the last check is an
   * hour old, then renews the ID token. A lost package ends the user's sessions,
   * so the check answers with a custom token that replaces this one.
   */
  private async checkEntitlement(): Promise<void> {
    const now = Date.now();
    if (now - this.entitlementAttemptAt < ENTITLEMENT_CHECK_INTERVAL_MS) return;
    try {
      const [source, checkedAt] = await Promise.all([
        this.auth.claim('identitySource'),
        this.auth.claim('packageAccessCheckedAt'),
      ]);
      if (source !== 'cognito') return;
      if (typeof checkedAt === 'number' && now - checkedAt < ENTITLEMENT_CHECK_INTERVAL_MS) return;
      const token = await this.auth.idToken();
      if (!token) return;
      this.entitlementAttemptAt = now;
      const result = await firstValueFrom(
        this.http.post<{ token?: string }>('/auth/entitlement', null, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      );
      if (result.token) await this.auth.loginWithCustomToken(result.token);
      else await this.auth.refreshIdToken();
    } catch {
      // The API keeps honoring the lease from the last successful check.
    }
  }

  private loadPrincipal(user: User): Promise<void> {
    // Login and the ID-token observer both load the principal for the same sign-in; share one request.
    const inFlight = this.principalLoad;
    if (inFlight && inFlight.uid === user.uid) return inFlight.load;
    // Another account's load still in flight must not land after this one.
    const generation = ++this.identityGeneration;
    const load = this.fetchPrincipal(user, generation).finally(() => {
      if (this.principalLoad?.load === load) this.principalLoad = null;
    });
    this.principalLoad = { uid: user.uid, load };
    return load;
  }

  private async fetchPrincipal(user: User, generation: number): Promise<void> {
    try {
      const principal = await firstValueFrom(this.http.get<AuthPrincipal>('/api/v1/auth/me'));
      if (generation !== this.identityGeneration) return;
      this.sessionState.set({
        status: 'authenticated',
        email: user.email ?? principal.subject,
        principal,
        name: user.displayName ?? undefined,
        // Present only for Cognito identities; the roles API omits it otherwise.
        organization: principal.organization,
      });
    } catch {
      if (generation !== this.identityGeneration) return;
      this.sessionState.set({
        status: 'error',
        message: 'Kirjautumistietoja ei voitu vahvistaa.',
      });
    }
  }

  /** Leaves the session anonymous and drops any principal load the signed-out user started. */
  private signOutLocally(): void {
    this.identityGeneration++;
    this.principalLoad = null;
    this.entitlementAttemptAt = 0;
    this.sessionState.set({ status: 'anonymous' });
  }

  private authenticatedState(): Extract<AuthSessionState, { status: 'authenticated' }> | null {
    const state = this.sessionState();
    return state.status === 'authenticated' ? state : null;
  }

  private markReady(): void {
    if (!this.readyResolved) {
      this.readyResolved = true;
      this.resolveReady();
    }
  }
}
